import React from 'react'
import { Box, HStack, Image, Text, VStack } from '@chakra-ui/react'
import type { MatchingChallengeEntity } from '@kuow/core'
import { clamp01, formatCurrency, getTimeComponents } from '@kuow/core'
import StarsIcon from '../../assets/images/stars.svg'

const HeartMark: React.FC<{ showStars: boolean; primaryMuted: string; secondaryColor: string }> = ({
  showStars,
  primaryMuted,
  secondaryColor,
}) => {
  return (
    <Box 
      aria-hidden="true" 
      position="relative" 
      w="42px"
      aspectRatio="4/3"
      flexShrink={0}
      css={{
        '@container (min-width: 280px)': {
          width: '60px'
        },
        '@container (min-width: 350px)': {
          width: '80px'
        }
      }}
    >
      {showStars && (
        <Box
          position="absolute"
          top="-600%"
          left="-287.5%"
          w="1905%"
          h="1660%"
          mixBlendMode="screen"
          pointerEvents="none"
          zIndex={0}
        >
          <Image src={StarsIcon} alt="" w="full" h="full" />
        </Box>
      )}
      <Box position="relative" zIndex={1} w="full" h="full">
        <Box
          asChild
          aria-hidden="true"
          w="72.5%"
          h="85%"
          color={primaryMuted}
          position="absolute"
          left="2.5%"
          top="4.17%"
          transform="rotate(-6deg)"
        >
          <svg viewBox="0 0 58 51">
            <path
              d="M28.8582 7.94927C32.7601 2.59519 44.0278 -4.89841 53.3846 4.41885C62.7813 13.776 54.5893 25.2921 49.7704 30.0905L28.909 50.8634V50.9637L28.8586 50.9136L28.8083 50.9637V50.8634L7.94684 30.0905C3.02727 25.1918 -5.06414 13.776 4.33268 4.41885C13.6892 -4.89819 24.956 2.59521 28.8582 7.94927Z"
              fill="currentColor"
            />
          </svg>
        </Box>
        <Box
          asChild
          aria-hidden="true"
          w="72.5%"
          h="85%"
          color={secondaryColor}
          position="absolute"
          left="25%"
          top="10.83%"
          transform="rotate(18deg)"
          overflow="visible"
        >
          <svg viewBox="0 0 58 51">
            <path
              d="M28.8582 7.94927C32.7601 2.59519 44.0278 -4.89841 53.3846 4.41885C62.7813 13.776 54.5893 25.2921 49.7704 30.0905L28.909 50.8634V50.9637L28.8586 50.9136L28.8083 50.9637V50.8634L7.94684 30.0905C3.02727 25.1918 -5.06414 13.776 4.33268 4.41885C13.6892 -4.89819 24.956 2.59521 28.8582 7.94927Z"
              fill="currentColor"
            />
          </svg>
        </Box>
      </Box>
    </Box>
  )
}

export interface ChallengeProgressProps {
  challenge: MatchingChallengeEntity,
  showStars: boolean,
  showHeadline: boolean
  primaryColor?: string
  secondaryColor?: string
  textColor?: string
  showBackground?: boolean
  /**
   * Max width for the widget container.
   * Defaults to a compact size for embeds; can be overridden for previews/layouts.
   */
  maxW?: React.ComponentProps<typeof Box>['maxW']
}

export const ChallengeProgress: React.FC<ChallengeProgressProps> = ({
  challenge,
  showStars,
  showHeadline,
  primaryColor = 'river',
  secondaryColor,
  textColor,
  showBackground = true,
  maxW = 'sm',
}) => {
  if (challenge.status !== 'active') return null

  const primarySolid = `${primaryColor}.solid`
  const primaryMuted = `${primaryColor}.muted`
  const primaryEmphasized = `${primaryColor}.emphasized`
  const resolvedSecondaryColor = secondaryColor ?? 'river.inverted'
  const resolvedTextColor = textColor ?? `${primaryColor}.inverted`

  const headline = challenge.headline.trim() ? challenge.headline.trim() : null
  const supporterName = challenge.supporter_name || null
  const multiplierWording = challenge.multiplier > 1 ? `${challenge.multiplier}x MATCHING` : 'MATCHING'
  const challengeVerb = challenge.challenge_verb.trim() ? challenge.challenge_verb.trim() : 'is'

  const maxMatch = (challenge.supporter_match_maximum ?? 0) > 0 ? challenge.supporter_match_maximum : null
  const showProgress = maxMatch !== null

  const timeRemaining = getTimeComponents(challenge.minutes_remaining)
  const showTimeRemaining =
    Boolean(challenge.show_complete_at) &&
    timeRemaining !== null &&
    Number.parseInt(timeRemaining.hours, 10) <= 24

  const showFallbackMessaging = !showProgress && !showTimeRemaining

  const progressPercent = (() => {
    if (!showProgress || !maxMatch) return 0
    const raw = challenge.progress_amount / maxMatch
    const clamped = clamp01(raw)
    return clamped < 0.03 ? 0 : clamped
  })()

  return (
    <Box
      borderRadius="24px"
      overflow="hidden"
      bg={showBackground ? primarySolid : 'transparent'}
      color={resolvedTextColor}
      px={4}
      py={3}
      fontFamily="'Montserrat', sans-serif"
      fontStyle="italic"
      position="relative"
      maxW={maxW}
      minW="0"
      w="full"
      containerType="inline-size"
    >
      <VStack gap={2} align="center" justifyContent="flex-start" position="relative" zIndex={1}>
        {headline && showHeadline && (
          <Text
            textAlign="center"
            fontSize="20px"
            fontWeight="extrabold"
            textTransform="uppercase"
            lineHeight="1.1"
            position="relative"
            zIndex={2}
          >
            {headline}
          </Text>
        )}

        <HStack gap={3} align="center" color={resolvedTextColor} minW={0} w="fit-content" position="relative" zIndex={2}>
          <HeartMark showStars={showStars} primaryMuted={primaryMuted} secondaryColor={resolvedSecondaryColor} />
          <VStack gap="2px" align="start" flexGrow={1} flexShrink={1} minW={0}>
            {supporterName && (
              <Text fontSize="16px" fontWeight="bold" lineHeight="1" wordBreak="break-word" textAlign="left">
                {supporterName}
              </Text>
            )}

            <HStack gap={1} flexWrap="nowrap" lineHeight="21px" whiteSpace="nowrap">
              {supporterName && (
                <Text fontSize="16px" fontWeight="medium" lineHeight="1">
                  {challengeVerb}
                </Text>
              )}
              <Text
                fontSize="18px"
                fontWeight="bold"
                lineHeight="1"
                css={{
                  '@container (min-width: 280px)': {
                    fontSize: '22px'
                  },
                  '@container (min-width: 350px)': {
                    fontSize: '26px'
                  }
                }}
              >
                {multiplierWording}
              </Text>
            </HStack>

            {maxMatch !== null && (
              <Text fontSize="16px" fontWeight="medium" lineHeight="1" mt="0" display="flex" gap="6px" verticalAlign="bottom">
                <Box as="span" lineHeight="1.1">up to </Box>
                <Box as="span" whiteSpace="nowrap" display="flex" gap="0px">
                  <Box as="span" opacity={0.6}>$</Box>
                  <Box
                    as="span"
                    fontWeight="bold"
                    fontSize="20px"
                    css={{
                      '@container (min-width: 280px)': {
                        fontSize: '24px'
                      },
                      '@container (min-width: 350px)': {
                        fontSize: '26px'
                      }
                    }}
                  >
                    {formatCurrency(maxMatch)}
                  </Box>
                </Box>
              </Text>
            )}

            {showFallbackMessaging && (
              <Text fontSize="16px" fontWeight="medium" lineHeight="21px">
                <Box as="span" fontWeight="bold">ALL</Box> donations today!
              </Text>
            )}
          </VStack>
        </HStack>

        {showProgress && (
          <Box
            borderWidth="3px"
            borderColor={resolvedSecondaryColor}
            borderRadius="24px"
            overflow="hidden"
            h="20px"
            bg={primaryEmphasized}
            w="full"
            position="relative"
            zIndex={2}
          >
            <Box h="full" w={`${progressPercent * 100}%`} bg={primaryMuted} />
          </Box>
        )}

        {showTimeRemaining && timeRemaining && (
          <HStack gap={2.5} justify="center" align="center" w="full" position="relative" zIndex={2}>
            <Text fontSize="18px" fontWeight="medium" lineHeight="1" textAlign="right">
              for the next
            </Text>
            <HStack gap="3px" align="center">
              <Box
                w="54px"
                h="41px"
                borderRadius="4px"
                borderWidth="1px"
                borderColor={primarySolid}
                bg={`${primaryColor}.inverted`}
                color={primarySolid}
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontStyle="normal"
                fontWeight="bold"
                fontSize="34px"
                lineHeight="21px"
                overflow="hidden"
                position="relative"
                _before={{
                  content: '""',
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  right: '0',
                  height: '50%',
                  bg: primaryMuted,
                  opacity: 0.2,
                  zIndex: 1,
                }}
                _after={{
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '0',
                  right: '0',
                  height: '1px',
                  bg: primaryMuted,
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                }}
              >
                <Box position="relative" zIndex={3}>
                  {timeRemaining.hours}
                </Box>
              </Box>
              <Text fontStyle="normal" fontWeight="bold" fontSize="24px" lineHeight="18px">
                :
              </Text>
              <Box
                w="54px"
                h="41px"
                borderRadius="4px"
                borderWidth="1px"
                borderColor={primarySolid}
                bg={`${primaryColor}.inverted`}
                color={primarySolid}
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontStyle="normal"
                fontWeight="bold"
                fontSize="34px"
                lineHeight="21px"
                overflow="hidden"
                position="relative"
                _before={{
                  content: '""',
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  right: '0',
                  height: '50%',
                  bg: primaryMuted,
                  opacity: 0.2,
                  zIndex: 1,
                }}
                _after={{
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '0',
                  right: '0',
                  height: '1px',
                  bg: primaryMuted,
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                }}
              >
                <Box position="relative" zIndex={3}>
                  {timeRemaining.minutes}
                </Box>
              </Box>
            </HStack>
          </HStack>
        )}
      </VStack>
    </Box>
  )
}


